import { useEffect, useRef, useState } from "react";
import { getStoreById } from "../../lib/admin";
import { Store } from "../../types";
import { closeSyncWindow, syncWindowRemainingMs } from "./syncWindow";

const POLL_MS = 10_000;
const MAX_FAILURES = 5;
const MAX_POLLING_MS = 5 * 60_000;

export const storeIsSyncing = (store: Store | null | undefined) =>
    store?.syncState === 1 || store?.syncV2IsRunning === 1;

export function useStoreSyncState(store: Store | null, onStore: (store: Store) => void) {
    const [tick, setTick] = useState(0);
    const onStoreRef = useRef(onStore);
    onStoreRef.current = onStore;

    const sawRunning = useRef(false);
    const failures = useRef(0);
    const polling = useRef(false);
    const askNow = useRef(false);
    const startedAt = useRef(0);
    const trackedStoreId = useRef<number | null>(null);

    useEffect(() => {
        const storeId = store?.id;
        if (!storeId) return;

        if (trackedStoreId.current !== storeId) {
            trackedStoreId.current = storeId;
            sawRunning.current = false;
            failures.current = 0;
            polling.current = false;
            askNow.current = false;
            startedAt.current = 0;
        }

        const running = storeIsSyncing(store);
        if (running) {
            sawRunning.current = true;
            polling.current = true;
        }

        if (!running && syncWindowRemainingMs(storeId) > 0) {
            polling.current = true;
        }
        else if (!running) {
            if (polling.current) {
                closeSyncWindow(storeId);
                sawRunning.current = false;
                polling.current = false;
                failures.current = 0;
                startedAt.current = 0;
            }
            return;
        }

        if (!startedAt.current) startedAt.current = Date.now();

        if (failures.current >= MAX_FAILURES) return;
        if (Date.now() - startedAt.current > MAX_POLLING_MS) return;

        let cancelled = false;
        const retry = () => { if (!cancelled) setTick(t => t + 1); };
        const delay = askNow.current ? 0 : POLL_MS;
        askNow.current = false;

        const timer = setTimeout(async () => {
            if (document.visibilityState === "hidden") return;
            try {
                const fresh = await getStoreById(storeId);
                if (cancelled) return;
                if (!fresh) { failures.current++; return retry(); }
                failures.current = 0;

                const finished = sawRunning.current && !storeIsSyncing(fresh);
                if (finished) {
                    closeSyncWindow(storeId);
                    sawRunning.current = false;
                    polling.current = false;
                    startedAt.current = 0;
                }

                onStoreRef.current(fresh);
            }
            catch {
                if (cancelled) return;
                failures.current++;
                retry();
            }
        }, delay);

        return () => { cancelled = true; clearTimeout(timer); };
    }, [store, tick]);

    useEffect(() => {
        const onVisible = () => {
            if (document.visibilityState !== "visible") return;
            askNow.current = true;
            setTick(t => t + 1);
        };
        document.addEventListener("visibilitychange", onVisible);
        return () => document.removeEventListener("visibilitychange", onVisible);
    }, []);
}
